/**
 * * 新增配置项 `dvReferenceLine` 来配置瀑布图的相邻柱子间参考线
 *
 * - 仅在 `dvType: 'waterfall'` 时生效
 * - 只考虑竖向瀑布图
 * - 仅配置在第二个柱状图才有效
 *
 * @example
 * ```javascript
 * {
 *   type: 'bar',
 *   dvType: 'waterfall',
 *   stack: 'waterfall'
 *   dvReferenceLine: {
 *     show: true,
 *     color: 'gray',
 *   },
 * }
 * ```
 */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { type default as ExtensionAPI } from 'echarts/lib/core/ExtensionAPI';
import { Group, Line, type Rect } from 'echarts/lib/util/graphic';
import {
  initProps,
  updateProps,
  removeElementWithFadeOut
} from 'echarts/lib/animation/basicTrasition';
import type { WaterfallSeriesModel, WaterfallView, RectLayout, Element } from './type';
import { filterWaterfallSeries, eachWaterfall } from './helper';

type LineLayoutInfo = { minX: number; minY: number; maxX: number; maxY: number };

function buildLines(
  group: Group,
  layoutInfos: LineLayoutInfo[],
  seriesModel: WaterfallSeriesModel
) {
  const isAnimationEnabled = seriesModel.isAnimationEnabled();
  const cfg = seriesModel.get('dvReferenceLine');

  // ! 只考虑竖向瀑布图，即水平参考线
  // eslint-disable-next-line complexity
  layoutInfos.slice(0, layoutInfos.length - 1).forEach((layoutInfoTmp, index) => {
    const nextLayoutInfoTmp = layoutInfos[index + 1];
    const x1 = layoutInfoTmp.maxX;
    let y1: number;
    const x2 = nextLayoutInfoTmp.minX;
    let y2: number;

    // ! 小数不能直接用等于判断
    if (
      Math.abs(layoutInfoTmp.minY - nextLayoutInfoTmp.minY) < 1 ||
      Math.abs(layoutInfoTmp.minY - nextLayoutInfoTmp.maxY) < 1
    ) {
      // eslint-disable-next-line no-multi-assign
      y1 = y2 = layoutInfoTmp.minY;
    } else if (
      Math.abs(layoutInfoTmp.maxY - nextLayoutInfoTmp.minY) < 1 ||
      Math.abs(layoutInfoTmp.maxY - nextLayoutInfoTmp.maxY) < 1
    ) {
      // eslint-disable-next-line no-multi-assign
      y1 = y2 = layoutInfoTmp.maxY;
    } else {
      //
    }
    if (!y1 || !y2) {
      return;
    }

    const opts = {
      shape: {
        x1,
        y1,
        x2,
        y2
      },
      style: {
        lineWidth: 1,
        lineDash: 'dashed',
        stroke: cfg?.color ?? 'gray'
      },
      z: (seriesModel.getData().getItemGraphicEl(index) as Rect).z + 1
    } as const;
    const name = `dvRefLine-${index}-${index + 1}`;
    let lineEl = group.childOfName(name) as Element<Line>;

    if (!lineEl) {
      lineEl = new Line({
        name,
        ...opts
      });

      group.add(lineEl);

      if (isAnimationEnabled) {
        lineEl.attr({ style: { opacity: 0 } });

        initProps(lineEl, {}, seriesModel, {
          cb: () => {
            lineEl.attr({ style: { opacity: 1 } });
          }
        });
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (isAnimationEnabled) {
        updateProps(lineEl, opts, seriesModel);
      } else {
        lineEl.attr(opts);
      }
    }

    lineEl.__dvAlive = true;
  });
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function renderReferenceLine(seriesModels: WaterfallSeriesModel[], api: ExtensionAPI) {
  const seriesView = api.getViewOfSeriesModel(seriesModels[0]) as WaterfallView;
  let referenceLineGroup = seriesView.__dvReferenceLineGroup;
  if (!referenceLineGroup) {
    referenceLineGroup = new Group({ name: 'dvReferenceLine' });
    seriesView.__dvReferenceLineGroup = referenceLineGroup;
    seriesView.group.add(referenceLineGroup);
  }

  referenceLineGroup.eachChild((childEl: Element<Line>) => {
    childEl.__dvAlive = false;
  });

  // ! 仅配置在第二个柱状图才有效
  if (seriesModels[0]?.get('dvReferenceLine')?.show) {
    const layoutInfos = [] as LineLayoutInfo[];

    seriesModels.forEach(seriesModel => {
      const data = seriesModel.getData();

      data.each(idx => {
        const layoutInfoTmp =
          layoutInfos[idx] ||
          (layoutInfos[idx] = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

        const itemLayout = data.getItemLayout(idx) as RectLayout;
        let minX = itemLayout.x;
        let minY = itemLayout.y;
        let maxX = minX + itemLayout.width;
        let maxY = minY + itemLayout.height;
        if (minX > maxX) {
          [minX, maxX] = [maxX, minX];
        }
        if (minY > maxY) {
          [minY, maxY] = [maxY, minY];
        }

        layoutInfoTmp.minX = Math.min(
          layoutInfoTmp.minX,
          !Number.isNaN(Number(minX)) ? minX : layoutInfoTmp.minX
        );
        layoutInfoTmp.minY = Math.min(
          layoutInfoTmp.minY,
          !Number.isNaN(Number(minY)) ? minY : layoutInfoTmp.minY
        );
        layoutInfoTmp.maxX = Math.max(
          layoutInfoTmp.maxX,
          !Number.isNaN(Number(maxX)) ? maxX : layoutInfoTmp.maxX
        );
        layoutInfoTmp.maxY = Math.max(
          layoutInfoTmp.maxY,
          !Number.isNaN(Number(maxY)) ? maxY : layoutInfoTmp.maxY
        );
      });
    });

    buildLines(referenceLineGroup, layoutInfos, seriesModels[0]);
  }

  // remove
  referenceLineGroup
    .children()
    .slice()
    .forEach((childEl: Element<Line>) => {
      if (!childEl.__dvAlive) {
        removeElementWithFadeOut(childEl, seriesModels[0]);
      }
    });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (ecModal, api) => {
    eachWaterfall(
      filterWaterfallSeries(ecModal.getSeries() as unknown as WaterfallSeriesModel[]),
      seriesModels => renderReferenceLine(seriesModels, api)
    );
  });
}
